import React from 'react';
import { Container, Row, Col, Form, Button, Spinner, Alert, Image } from 'react-bootstrap';
import PropTypes from 'prop-types';

import { postPledge, getMunicipalities } from '../../utils/api';
import { findIntersectingFeatures } from '../../utils/intersect';

import AddressLookup from '../../components/AddressLookup/AddressLookup';
import CurrencyInput from '../../components/CurrencyInput/CurrencyInput';
import GetStartedSelect from '../../components/GetStartedSelect/GetStartedSelect';
import Assessment from '../../components/Assessment/Assessment';
import config from '../../config';

import styles from './Registration.module.css';

export const RegistrationTypes = {
  homeowner: 'homeowner',
  renter: 'renter',
  business: 'business',
  other: 'other',
  indigenous: 'indigenous',
  nonprofit: 'nonprofit',
};

class Registration extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      errorMsg: '',
      showAssessment: false,
      
      address: '',
      lat: null,
      lng: null,
      postalCode: '',
      municipality: null,
      ruralArea: null,
      rural: false,
      addressSelected: false,
      
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyName: '',
      description: '',
      community: '',
      
      value: 0,
      rent: 0,
      
      newsletter: true,
    };
  }

  handleChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleCurrencyChange = (name, value) => {
    this.setState({ [name]: value });
  };

  handleAddressChange = async (address, geoCode) => {
    if (!geoCode) {
      this.setState({
        address,
        addressSelected: false
      });
      return;
    }

    // Extract postal code from address components
    const postalCode = geoCode.address_components.find(c => 
      c.types.includes('postal_code')
    )?.long_name || '';

    const lat = geoCode.geometry.location.lat();
    const lng = geoCode.geometry.location.lng();

    let municipality = null;
    let ruralArea = null;

    try {
      this.setState({ loadingRegion: true });
      
      const intersectingFeatures = await findIntersectingFeatures([lng, lat]);
      
      // If address doesn't intersect with any GeoJSON, it's outside BC
      if (!intersectingFeatures || intersectingFeatures.length === 0) {
        ruralArea = { name: 'rgn_0', regionId: 'rgn_0' };
      } else {
        // Prioritize municipality polygons (*_Municipalities.geojson)
        const municipalityIntersection = intersectingFeatures.find(
          ({ fileName }) => fileName.toLowerCase().includes('municipalities')
        );
        
        // Fall back to regional boundary polygons
        const featureWithRegion = municipalityIntersection || intersectingFeatures.find(
          ({ feature }) => feature.properties?.rgn_ID
        );
        
        if (featureWithRegion) {
          const props = featureWithRegion.feature.properties;
          const regionId = props.rgn_ID;
          const defaultRate = 5;
          
          // If it's a municipality (has AA_NAME), create municipality object
          if (props.AA_NAME) {
            const municipalitiesData = await getMunicipalities();
            const mun = municipalitiesData.municipalities.find(
              m => m.regionId === regionId && (
                m.label === props.ABRVN || 
                m.label === props.AA_NAME ||
                props.AA_NAME.includes(m.label)
              )
            );
            
            municipality = {
              label: props.AA_NAME,
              value: props.AA_ID,
              regionId: regionId,
              mun_rate: mun ? parseFloat(mun.mun_rate) || defaultRate : defaultRate,
              name: props.AA_NAME
            };
          } else {
            // It's a rural/boundary area
            ruralArea = {
              name: regionId,
              regionId: regionId,
              rural_rate: defaultRate
            };
          }
        } else {
          // Intersects but no rgn_ID - treat as outside BC
          ruralArea = { name: 'rgn_0', regionId: 'rgn_0' };
        }
      }
    } catch (error) {
      console.error('Error finding region:', error);
      this.setState({
        loadingRegion: false,
        regionError: 'Unable to determine region. Please try again or contact support if the issue persists.'
      });
      return;
    }

    this.setState({
      address,
      postalCode,
      lat,
      lng,
      municipality,
      ruralArea,
      rural: !municipality && !!ruralArea,
      addressSelected: true,
      loadingRegion: false,
      regionError: null
    });
  };

  handleAddressSelectionChange = (selected) => {
    this.setState({ addressSelected: selected });
  }

  handleNewsletterChange = () => {
    this.setState({ newsletter: !this.state.newsletter });
  }

  isFormReadyToSubmit = () => {
    const { type, addressSelected, value, rent, companyName, community } = this.state;
    
    if (type === RegistrationTypes.homeowner) {
      return addressSelected && value && parseFloat(value) > 0;
    }
    
    if (type === RegistrationTypes.renter) {
      return addressSelected && rent && parseFloat(rent) > 0;
    }
    
    if (type === RegistrationTypes.business || type === RegistrationTypes.nonprofit) {
      return companyName && companyName.trim().length > 0 && addressSelected;
    }
    
    if (type === RegistrationTypes.other) {
      return addressSelected;
    }
    
    if (type === RegistrationTypes.indigenous) {
      return community && community.trim().length > 0;
    }
    
    return true;
  }

  handleSubmit = async event => {
    event.preventDefault();

    this.setState({ loading: true, errorMsg: '' });

    const { loading, errorMsg, showAssessment, email, newsletter, community, ...form } = this.state;

    const submitObject = {
      createdAt: Date.now(),
      amount: 0,
      currency: '',
      email,
      regionName: '',
      term: '',
      description: form.description || '',
      public: '',
      publicMap: 0,
      total: '',
      registration: {
        type: this.props.type,
        unit: form.unit || '',
        address: form.address || '',
        lat: form.lat,
        lng: form.lng,
        postalCode: form.postalCode || '',
        municipality: form.municipality || '',
      },
      newsletter,
      community
    };

    try {
      const requiresPledge = [RegistrationTypes.business, RegistrationTypes.other, RegistrationTypes.indigenous, RegistrationTypes.nonprofit].includes(this.props.type);
      if (requiresPledge) {
        await postPledge(submitObject);
      }
      this.props.onSuccess(this.state);
    } catch (error) {
      this.setState({ loading: false, errorMsg: error.message });
    }
  }

  toggleAssessment = event => {
    if (event?.preventDefault) event.preventDefault();
    this.setState({ showAssessment: !this.state.showAssessment });
  }

  render() {
    const { subText, image, type, submitText, buttonColor } = this.props;
    const isBusinessEventIndigenous = [RegistrationTypes.business, RegistrationTypes.other, RegistrationTypes.indigenous, RegistrationTypes.nonprofit].includes(type);
    const isHomeownerRenter = [RegistrationTypes.homeowner, RegistrationTypes.renter].includes(type);

    return (
      <Container>
        <div className="marginBottom50">
          <GetStartedSelect type={type} />
        </div>
        <div className="marginBottom50 font-thin">
          {subText}
          {' '}
          {type === RegistrationTypes.homeowner &&
            <a href={`${config.domains.marketing}/how-reciprocity-works/#what-next`} target="_blank" rel="noreferrer">
              <button className="btn btn-link align-baseline p-0"> Where would my dollars go?</button>
            </a>
          }
          {type === RegistrationTypes.renter &&
          <span>
            Learn more about&nbsp;
            <a className="font-thin" href={`${config.domains.marketing}/how-reciprocity-works/#what-next`} target='_blank' rel="noreferrer">
              <button className="btn btn-link align-baseline p-0"> where Reciprocity Payments go.</button>
            </a>
          </span>
          }
        </div>

        <Row className="marginBottom50">
          <Col className="text-center reciprocityLightBlueText" lg={4}>
            <Image className={`${styles.thumbnail} mb-4`} src={image} thumbnail />
            <h3>reciprocity.</h3>
            {isHomeownerRenter && <h3>home.</h3>}
            {type === RegistrationTypes.business && <h3>business.</h3>}
            {type === RegistrationTypes.nonprofit && <h3>organization.</h3>}
            {type === RegistrationTypes.other && <h3>event.</h3>}
          </Col>
          <Col lg={8}>
            {(type === RegistrationTypes.business || type === RegistrationTypes.other || type === RegistrationTypes.nonprofit) &&
              <div className='font-thin mb-4'>
                <p>
                  Because this is so variable, we don't have a calculator for this. <strong>We'll be in touch personally to work on next steps customized to your {type === RegistrationTypes.other ? 'event' : (type === RegistrationTypes.business ? 'business' : 'organization')}.</strong> At this point in time, we are only working with businesses and events operating primarily on Southern Vancouver Island. If you are located elsewhere, please reach out to us so we can let you know when we are up and running in your area.
                </p>
                <a className='btn btn-link align-baseline p-0' href={`${config.domains.marketing}/how-reciprocity-works/#what-next`} target="_blank" rel="noreferrer">Where would my dollars go?</a>
              </div>
            }

            <Form onSubmit={this.handleSubmit}>
              {isBusinessEventIndigenous &&
                <div>
                  <Row className="mb-3">
                    <Col xs={12} sm={6} className="mb-3 mb-sm-0">
                      <Form.Group controlId="firstName">
                        <Form.Label>First Name *</Form.Label>
                        <Form.Control name="firstName" onChange={this.handleChange} type="text" required />
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Form.Group controlId="lastName">
                        <Form.Label>Last Name *</Form.Label>
                        <Form.Control name="lastName" onChange={this.handleChange} type="text" required />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col xs={12} sm={6} className="mb-3 mb-sm-0">
                      <Form.Group controlId="email">
                        <Form.Label>E-Mail Address *</Form.Label>
                        <Form.Control name="email" onChange={this.handleChange} type="email" required />
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Form.Group controlId="phone">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control name="phone" onChange={this.handleChange} type="text" />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              }

              {type === RegistrationTypes.indigenous &&
                <Row className="mb-3">
                  <Col xs={12}>
                    <Form.Group controlId="community">
                      <Form.Label>Let us know which First Nation or Indigenous community you're from: *</Form.Label>
                      <Form.Control name="community" onChange={this.handleChange} type="text" required />
                    </Form.Group>
                  </Col>
                </Row>
              }

              {isHomeownerRenter &&
                <Row className="mb-3">
                  <Col xs={12} sm={9} className="mb-3 mb-sm-0">
                    <Form.Group controlId="address">
                      <Form.Label>Address * <small className="text-muted">(Select from dropdown)</small></Form.Label>
                      <AddressLookup onChange={this.handleAddressChange} onAddressChange={this.handleAddressSelectionChange} required />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={3}>
                    <Form.Group controlId="unit">
                      <Form.Label>Unit Number</Form.Label>
                      <Form.Control name="unit" onChange={this.handleChange} type="text" />
                    </Form.Group>
                  </Col>
                </Row>
              }

              {(type === RegistrationTypes.business || type === RegistrationTypes.nonprofit) &&
                <Form.Group controlId="companyName" className="mb-3">
                  <Form.Label>{type === RegistrationTypes.business ? 'Company' : 'Organization'} Name *</Form.Label>
                  <Form.Control name="companyName" onChange={this.handleChange} type="text" required />
                </Form.Group>
              }

              {type === RegistrationTypes.business &&
                <Row className="mb-3">
                  <Col xs={12}>
                    <Form.Group controlId="businessAddress">
                      <Form.Label>Business Address (where you are headquartered) *</Form.Label>
                      <AddressLookup onChange={this.handleAddressChange} onAddressChange={this.handleAddressSelectionChange} required />
                    </Form.Group>
                  </Col>
                </Row>
              }

              {type === RegistrationTypes.other &&
                <Row className="mb-3">
                  <Col xs={12}>
                    <Form.Group controlId="eventAddress">
                      <Form.Label>Event Address (where your event will take place) *</Form.Label>
                      <AddressLookup onChange={this.handleAddressChange} onAddressChange={this.handleAddressSelectionChange} required />
                    </Form.Group>
                  </Col>
                </Row>
              }

              {type === RegistrationTypes.nonprofit &&
                <Row className="mb-3">
                  <Col xs={12}>
                    <Form.Group controlId="nonprofitAddress">
                      <Form.Label>Organization Address (where you are headquartered) *</Form.Label>
                      <AddressLookup onChange={this.handleAddressChange} onAddressChange={this.handleAddressSelectionChange} required />
                    </Form.Group>
                  </Col>
                </Row>
              }

              {type === RegistrationTypes.homeowner &&
                <Row className="mb-3">
                  <Col xs={12} sm={5} md={4} className="mb-3 mb-sm-0">
                    <Form.Group controlId="value">
                      <Form.Label>Current Property Value *</Form.Label>
                      <Form.Control as={CurrencyInput} name="value" onChange={this.handleCurrencyChange} value={this.state.value} required />
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={7} md={8} className='align-self-end'>
                    <div className={styles.info}>
                      <i className="fa fa-info-circle pr-2"></i>
                      <button className='btn btn-link align-baseline p-0' onClick={this.toggleAssessment}>Find your property value</button>
                    </div>
                  </Col>
                </Row>
              }

              {type === RegistrationTypes.renter &&
                <Form.Group controlId="rent" className="mb-3">
                  <Form.Label>Monthly Rent *</Form.Label>
                  <Form.Control as={CurrencyInput} name="rent" onChange={this.handleCurrencyChange} value={this.state.rent} required />
                </Form.Group>
              }

              {type !== RegistrationTypes.indigenous &&
                <Form.Group className="my-3">
                  <Form.Check className={styles.signUp} type='checkbox' id='signUp' label="Sign up for our newsletter"
                    checked={this.state.newsletter} onChange={this.handleNewsletterChange}
                  />
                </Form.Group>
              }

              {(type === RegistrationTypes.business || type === RegistrationTypes.nonprofit) &&
                <Form.Group controlId="description" className="mb-3">
                  <Form.Label>{type === RegistrationTypes.business ? 'Business' : 'Organization'} Description</Form.Label>
                  <Form.Control name="description" onChange={this.handleChange} as="textarea" rows={2} placeholder={`Tell us a bit about your ${type === RegistrationTypes.business ? 'business' : 'organization'} and where you operate`} />
                </Form.Group>
              }

              {type === RegistrationTypes.other &&
                <Form.Group controlId="description" className="mb-3">
                  <Form.Label>Event Description</Form.Label>
                  <Form.Control name="description" onChange={this.handleChange} as="textarea" rows={2} placeholder="Tell us a bit about your event or what you are up to" />
                </Form.Group>
              }

              <div className={styles.requiredTag}>* Required field</div>
              {this.state.errorMsg &&
                <Alert variant='danger'>{this.state.errorMsg}</Alert>
              }
              <Button className='mb-3' type='submit' style={{ '--button-color': buttonColor }} disabled={!this.isFormReadyToSubmit() || this.state.loading}>
                {this.state.loading &&
                  <span>
                    <Spinner as='span' animation='grow' size='sm' /> Processing Your Submission...
                  </span>
                }
                {!this.state.loading &&
                  <span>
                    {submitText}
                  </span>
                }
              </Button>
            </Form>
          </Col>
        </Row>
        <Assessment show={this.state.showAssessment} onHide={this.toggleAssessment} />
      </Container>
    )
  }
}

Registration.propTypes = {
  subText: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  image: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  submitText: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  onSuccess: PropTypes.func.isRequired,
  buttonColor: PropTypes.string
};

export default Registration;